import { useCallback, useEffect, useContext } from "react";
import "./earnings.css";
import { useState } from "react";
import { ethers } from "ethers";
import { Earning, myNFT } from "./types";
import { OmmuseClient } from "../../proto/api_grpc_web_pb.js";
import { QueryTransactionsRequest } from "../../proto/api_pb.js";
import { QueryTransactionsResponse } from "../../proto/api_pb.js";
import { FractionalTransactionState } from "../../proto/api_pb.js";
import { FractionalTransaction } from "../../proto/api_pb.js";
import { RpcError } from "grpc-web";
import { AuthContext, AuthContextInterface } from "../../contexts";
import SplitMainABI from "../../contracts-data/SplitMain.json";
import UserStatusModal from "../Modal/UserStatusModal";
import WalletDetails from "./WalletDetails";
import NftTable from "./NftTable";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";

const splitMainAddress = "0x2ed6c4B5dA6378c7897AC67Ba9e43102Feb694EE";
export const openseaURL = process.env.REACT_APP_OPENSEA_URL ?? "";
export const erc721ContractAddress = process.env.REACT_APP_ECR721 ?? "";
const etherscanURL = process.env.REACT_APP_ETHERSCAN_URL ?? "";

let splitMainContract: ethers.Contract;
if (window.ethereum) {
  const provider =
    new ethers.providers.Web3Provider(window.ethereum!) ?? undefined;
  if (provider) {
    const signer = provider.getSigner();
    splitMainContract = new ethers.Contract(
      splitMainAddress,
      SplitMainABI.abi,
      signer
    );
  }
}

function Earnings({
  username,
  connectedWallet,
}: {
  username: string | undefined;
  connectedWallet: string | undefined;
}): JSX.Element {
  const { getValidAccessToken } = useGetValidAccessToken();
  const [earnings, setEarnings] = useState<Earning[]>([]);
  const [wBalance, setWBalance] = useState<string>("0");

  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const authContext = useContext<AuthContextInterface>(AuthContext);

  const getStateStringFromState = (state: number): string => {
    switch (state) {
      case FractionalTransactionState.TRANSACTION_PENDING:
        return "pending";
      case FractionalTransactionState.TRANSACTION_AWAITING_MINT:
        return "minting";
      case FractionalTransactionState.TRANSACTION_MINTED:
        return "minted";
      case FractionalTransactionState.TRANSACTION_LISTED:
        return "for sale";
      case FractionalTransactionState.TRANSACTION_SOLD:
        return "sold";
      case FractionalTransactionState.TRANSACTION_FIRST_SALE_DISTRIBUTED:
        return "distributed sale revenue";
      case FractionalTransactionState.TRANSACTION_BURNED:
        return "nft burned";
    }
    return "unknown";
  };

  const withdrawBalance = async () => {
    setIsWithdrawing(true);
    // 1 means we want to withdraw ETH.
    if (splitMainContract) {
      try {
        const txn = await splitMainContract.withdraw(connectedWallet, 1, []);
        await txn.wait();
        const balance = ethers.utils.formatEther(
          await getSplitMainBalanceForAddress(connectedWallet!)
        );
        setWBalance(Number(balance ?? "0").toFixed(5));
      } catch (error) {
        console.log(error, "Withdraw failed");
      }
      setIsWithdrawing(false);
    }
  };

  const getSplitMainBalanceForAddress = async (address: string) => {
    if (splitMainContract) {
      return splitMainContract.getETHBalance(address);
    }
    return 0;
  };

  const getSplitShare = (
    transaction: FractionalTransaction,
    username: string
  ) => {
    let percent = 0;
    const part = transaction
      .getFractionalTransactionPartList()
      .find((part) => part.getUsername() === username);
    if (part) {
      percent = part.getPercent();
    }
    return percent;
  };

  const handleQueryTransactions = useCallback(
    async (err: RpcError, response: QueryTransactionsResponse) => {
      try {
        if (err) {
          console.log("Error while fetching earnings: " + err.message);
          alert("Error while fetching earnings");
        } else {
          if (authContext) {
            const transactions = response.getFractionalTransactionList();
            const earningsData: Earning[] = [];
            for (const transaction of transactions) {
              earningsData.push({
                id: transaction.getOmmuseTxnId(),
                token: transaction.getTokenId(),
                title: transaction.getTitle(),
                artist: transaction.getArtist(),
                state: getStateStringFromState(transaction.getState()),
                price: transaction.getInitialPrice(),
                splitBalance: Number(0).toFixed(5),
                // (NEEDS TO GET FIXED)
                // splitBalance:
                //   transaction.getSplitAddress() === ""
                //   ? Number(0).toFixed(5)
                //   : Number(
                //       ethers.utils.formatEther(
                //         await getSplitMainBalanceForAddress(
                //           transaction.getSplitAddress()
                //         )
                //       )
                //     ).toFixed(5),
                splitAddress: transaction.getSplitAddress(),
                splitPercent: getSplitShare(transaction, username!),
              });
            }
            earningsData.sort((a, b) => a.price - b.price);
            setEarnings(earningsData);
          }
        }
      } catch (error) {
        console.log(error);
        alert("Error while fetching contracts");
      }
    },
    [authContext, username]
  );

  useEffect(() => {
    (async () => {
      document.title = "Earnings - OmMuse";
      try {
        if (connectedWallet) {
          // (NEEDS TO GET FIXED)
          // const balance = ethers.utils.formatEther(
          //   await getSplitMainBalanceForAddress(connectedWallet)
          // );
          // setWBalance(Number(balance ?? "0").toFixed(5));
          const accessToken = await getValidAccessToken();
          const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
          const request = new QueryTransactionsRequest();
          request.addWalletAddress(connectedWallet);
          const metadata = {
            Authorization: `${accessToken}`,
            "Access-Control-Allow-Origin": "*",
          };
          ommuseClient.queryTransactions(
            request,
            metadata,
            handleQueryTransactions
          );
        }
      } catch (error) {
        console.log(error);
        alert("Error while fetching earnings");
      }
    })();
  }, [
    handleQueryTransactions,
    authContext,
    getValidAccessToken,
    connectedWallet,
  ]);

  const handleSort = () => {
    setEarnings([...earnings].reverse());
  };

  return (
    <>
      <UserStatusModal userId={authContext?.userId} targetPage={"/my-nfts"} />
      <NftTable
        title={"My NFTs"}
        handleSort={handleSort}
        earnings={earnings.filter((e) => e.splitPercent === 100)}
        columns={myNFT}
        etherscanURL={etherscanURL}
      />
      <NftTable
        title={"Collaborated NFTs"}
        handleSort={handleSort}
        earnings={earnings.filter((e) => e.splitPercent !== 100)}
        columns={[...myNFT, "Collaboration %", "Collaboration Contract"]}
        etherscanURL={etherscanURL}
      />
      <WalletDetails
        connectedWallet={connectedWallet}
        wBalance={wBalance}
        isWithdrawing={isWithdrawing}
        withdrawBalance={withdrawBalance}
      />
    </>
  );
}
export default Earnings;
